import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Rocket, Link2, AlertTriangle, Loader2, Save, ArrowRight } from 'lucide-react';
import { usePlanEditor } from './PlanEditorContext';
import DeterministicWhiteboxButton from '../DeterministicWhiteboxButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LaunchPanel — V15.10 · Étape 3 « Pièces & Lancer ».
 * Remplace l'ex-onglet Validation : récap du tracé, slots Matériauthèque,
 * puis lancement direct de la maquette blanche déterministe.
 */
export const LaunchPanel = ({ projectId, lotId, disabled, onOpenProduction }) => {
  const { walls, openings, rooms, calibration } = usePlanEditor();
  const [slots, setSlots] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastJob, setLastJob] = useState(null);

  const fetchSlots = useCallback(() => {
    if (!projectId) return;
    axios.get(`${API}/materialtheque/projects/${projectId}/unassigned-slots-count`, { withCredentials: true })
      .then((r) => setSlots(r.data)).catch(() => setSlots(null));
  }, [projectId]);
  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const markReviewed = useCallback(async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/plan-source/structure/mark-reviewed${lotId ? `?lot_id=${lotId}` : ''}`, null, { withCredentials: true });
      toast.success('Plan marqué comme revu');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  }, [projectId, lotId]);

  const hasGeometry = walls.length > 0;
  const checks = [
    { label: 'Murs', value: walls.length, ok: walls.length > 0 },
    { label: 'Menuiseries', value: openings.length, ok: openings.length > 0 },
    { label: 'Pièces', value: rooms.length, ok: rooms.length > 0 },
    { label: 'Échelle', value: calibration ? (calibration.distance_m ? `${Number(calibration.distance_m).toFixed(2)} m` : 'définie') : 'non définie', ok: !!calibration },
  ];

  return (
    <div className="border-t p-2.5 space-y-2.5" style={{ borderColor: 'var(--c-border)' }} data-testid="launch-panel">
      <h4 className="text-[12px] font-semibold flex items-center gap-1.5" style={{ color: 'var(--c-gold)' }}>
        <Rocket className="w-3.5 h-3.5" /> Prêt pour la Génération
      </h4>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px]" data-testid="launch-recap">
        {checks.map((c) => (
          <li key={c.label} className="flex justify-between gap-2">
            <span className="text-gray-400">{c.label}</span>
            <span className={c.ok ? 'text-teal-300' : 'text-amber-300'} data-testid={`launch-recap-${c.label.toLowerCase()}`}>{c.value}</span>
          </li>
        ))}
      </ul>
      {!calibration && (
        <p className="text-[10px] text-amber-300/90 flex items-start gap-1">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          Sans échelle (étape 1 · Mise à l'échelle), les dimensions utiliseront une valeur par défaut.
        </p>
      )}
      {slots?.has_materialtheque && (
        <div className="text-[10px] text-gray-300 flex items-center gap-1.5" data-testid="launch-slots">
          <Link2 className="w-3 h-3 text-emerald-400" />
          Matériauthèque : <span className="text-teal-300">{slots.assigned} assignés</span>
          {slots.unassigned > 0 && (
            <>
              · <span className="text-amber-300">{slots.unassigned} à remplir</span>
              <a href={`/hub/materials/${projectId}`} target="_blank" rel="noopener noreferrer"
                 className="underline text-amber-200 hover:text-white" data-testid="launch-slots-link">compléter →</a>
            </>
          )}
        </div>
      )}
      <DeterministicWhiteboxButton
        projectId={projectId} lotId={lotId}
        disabled={disabled || !hasGeometry}
        className="w-full"
        onDone={(job) => { setLastJob(job); markReviewed(); }}
        testId="launch-whitebox-btn"
      />
      {!lotId && (
        <p className="text-[10px] text-gray-500 italic">Projet mono-lot : lancez la maquette depuis Production.</p>
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={markReviewed} disabled={disabled || saving || !hasGeometry}
                className="flex-1 inline-flex items-center justify-center gap-1 h-7 text-[10.5px] rounded border border-white/10 text-gray-300 hover:bg-white/5 disabled:opacity-40"
                data-testid="launch-mark-reviewed-btn" title="Marquer le plan comme revu sans générer">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Marquer revu
        </button>
        {onOpenProduction && (
          <button type="button" onClick={onOpenProduction}
                  className={`flex-1 inline-flex items-center justify-center gap-1 h-7 text-[10.5px] rounded border ${lastJob ? 'border-teal-400/50 text-teal-200 bg-teal-500/10' : 'border-white/10 text-gray-300 hover:bg-white/5'}`}
                  data-testid="launch-open-production-btn">
            Production <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LaunchPanel;
